import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X, ExternalLink, Calendar, Package, Truck, ChevronLeft, ChevronRight, Plus, Pencil, FileText, Upload, AlertCircle, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';
import { cn } from '../lib/utils';
import * as utils from '../lib/utils.js';
import PartsPopup from './PartsPopup';
import ShipmentDocuments from './ShipmentDocuments';
import DeleteConfirmationPopup from './DeleteConfirmationPopup';
import { useNotification } from '../lib/NotificationProvider';
import { useUserData } from '../contexts/UserDataContext';
import { useQueryClient } from '@tanstack/react-query';

const LineItemForm = ({ lineItems = [] }) => {
  // Identity comes from the URL: /line-item?po=...&shipment=...
  const [searchParams] = useSearchParams();
  const po = searchParams.get('po');
  const shipment = searchParams.get('shipment');

  const location = useLocation();
  const navigate = useNavigate();
  const { markDataChanged } = useNotification();
  const { userData, refreshUserData } = useUserData();
  const queryClient = useQueryClient();

  const [lineItem, setLineItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dates');

  // Parts popup state
  const [partsAnchorRef, setPartsAnchorRef] = useState(null);
  const addPartsButtonRef = useRef(null);

  const [editingPart, setEditingPart] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [showPartsPopup, setShowPartsPopup] = useState(false);
  const [hasUnsavedPartChanges, setHasUnsavedPartChanges] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  //Documents
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [partToDelete, setPartToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingParts, setLoadingParts] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingParts, setIsSavingParts] = useState(false);
  const [isDeletingPart, setIsDeletingPart] = useState(false);
  const [showHideConfirm, setShowHideConfirm] = useState(false);
  const [isTogglingHide, setIsTogglingHide] = useState(false);

  // Identity-based chevron navigation. The dashboard passes an array of
  // {po, shipment} pairs reflecting the user's filtered/sorted view; the
  // chevrons walk that list without firing any network requests — each new
  // identity is just looked up in the bulk `lineItems` prop.
  const identityList = location.state?.identityList ?? [];
  const identityIndex = location.state?.identityIndex ?? -1;
  const hasNeighbors = identityList.length > 1 && identityIndex >= 0;

  // Add a ref for tracking refresh requests
  const hasRequestedRefresh = useRef(false);

  // Add console logging for userData
  useEffect(() => {
    console.log('LineItemForm: userData state:', {
      userData,
      hasRole: userData?.role,
      role: userData?.role,
      isEmployee: userData?.role === "Employee"
    });
    
    console.log('LineItemForm: canEditLineItems() =', canEditLineItems());
  }, [userData]);

  // Update canEditLineItems to use latest data 
  const canEditLineItems = () => {
    // If userData is null, data might be stale
    if (!userData && !hasRequestedRefresh.current) {
      console.log('LineItemForm: userData is null, requesting refresh');
      hasRequestedRefresh.current = true;
      refreshUserData();
      return false;
    }
    
    if (!userData) {
      console.log('LineItemForm: canEditLineItems - userData is null/undefined');
      return false;
    }
    
    // If data is marked as potentially stale, log a warning
    if (userData.potentiallyStale) {
      console.warn('LineItemForm: Using potentially stale user data for permissions');
    }
    
    const result = userData.role !== "Employee";
    return result;
  };

  // Helper function to check if user is a forwarder
  const isForwarderRole = () => {
    if (!userData) return false;
    return userData.role === "Forwarder";
  };

  const fetchParts = async (lineItem) => {
    try {
      console.log("Fetching parts for PO:", lineItem.title, "Shipment:", lineItem.related_shipment);
      const response = await utils.getParts(lineItem.title, lineItem.related_shipment);
      if (response.data && response.data.parts) {
        const parts = response.data.parts;
        console.log("Parts found:", parts);
        return parts; // Return the parts instead of updating state
      } else {
        console.log("No parts found");
        return []; // Return empty array
      }
    } catch(error) {
      console.error('Error fetching parts:', error);
      return []; // Return empty array on error
    }
  }

  const fetchDocuments = async (lineItem) => {
      if (!documents || documents.length === 0) {
        try {
          const response = await utils.getDocuments(`?${new URLSearchParams({ shipment: lineItem.related_shipment }).toString()}`);
          console.log('Documents fetched:', response.data.documents);
          const mappedDocs = response.data.documents.map(doc => ({
            id: doc.id,
            webUrl: doc.webUrl,
            fields: {
              FileLeafRef: doc.fields.FileLeafRef,
              DocumentType: doc.fields.DocumentType,
              FileSizeDisplay: doc.fields.FileSizeDisplay,
              Modified: doc.fields.Modified
            }
          }));
          console.log('mappedDocs', mappedDocs);
          setDocuments(mappedDocs);
        } catch (error) {
          console.error('Error fetching documents:', error);
          setDocuments([]);
        }
      }
      return documents || [];
  }

  // Resolve the current line item by stable identity (po + shipment) from the
  // bulk list. The bulk list lives in MainContent and is the single source of
  // truth — no per-mount targeted GET. While the user is editing, do not
  // overwrite local form state from background list updates.
  useEffect(() => {
    if (!po || !shipment) {
      setLoading(false);
      setLineItem(null);
      return;
    }
    if (!lineItems || lineItems.length === 0) {
      // Wait for the bulk list to arrive (full-refresh case).
      return;
    }
    const found = lineItems.find(li =>
      li.title?.trim() === po.trim() &&
      (li.related_shipment ?? '').toString().trim() === shipment.trim()
    );
    if (found) {
      setLineItem(found);
      if (!isEditing) {
        setFormData({ ...found, poNumber: found.title });
        setHasUnsavedPartChanges(false);
      }
      setLoading(false);
      fetchDocuments(found).then(setDocuments);
    } else {
      setLineItem(null);
      setLoading(false);
    }
  }, [po, shipment, lineItems, isEditing]);

  // New effect to fetch parts data as soon as lineItem is available
  useEffect(() => {
    if (lineItem) {
      const fetchPartsData = async () => {
        setLoadingParts(true);
        try {
          console.log("Fetching parts data for lineItem:", lineItem.title);
          const parts = await fetchParts(lineItem);
          // Update the lineItem state with the fetched parts
          setLineItem(prev => ({
            ...prev,
            part_numbers: parts
          }));
        } finally {
          setLoadingParts(false);
        }
      };
      
      fetchPartsData();
    }
  }, [lineItem?._id, lineItem?.title, lineItem?.related_shipment]);

  useEffect(() => {
    if (lineItem && activeTab === 'documents') {
      const fetchDocumentsData = async () => {
        setLoadingDocuments(true);
        setDocumentsError(null);
        try {
          const response = await utils.getDocuments(`?${new URLSearchParams({ shipment: lineItem.related_shipment }).toString()}`);
          if (response.data.documents) {
            setDocuments(response.data.documents);
          } else {
            setDocuments([]);
          }
        } catch (error) {
          console.error('Error fetching documents:', error);
          setDocumentsError('Failed to load documents. Please try again.');
        } finally {
          setLoadingDocuments(false);
        }
      };
      fetchDocumentsData();
    }
  }, [activeTab]); // Only depend on activeTab, not lineItem

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    try {
      const prevPo = lineItem.title;
      const hidden = formData.filter_record_out || false;
      const updatedLineItem = {
        ...lineItem,
        title: formData.poNumber,
        ignore_po_character_count: formData.ignore_po_character_count,
        hide_lineitem: hidden,
        filter_record_out: hidden,
      };

      const query = new URLSearchParams({
        shipment: lineItem.related_shipment,
        po: prevPo,
      }).toString();
      await utils.updateLineItem(updatedLineItem, query);

      setLineItem(updatedLineItem);
      setIsEditing(false);

      // Optimistically patch the bulk list so the form's identity-lookup
      // effect doesn't briefly overwrite our just-saved state with the stale
      // pre-refetch list. The invalidate that follows will reconcile against
      // the backend's authoritative copy.
      queryClient.setQueriesData(
        { queryKey: ['lineItems'] },
        (old) => Array.isArray(old)
          ? old.map(item =>
              item.title === prevPo && item.related_shipment === updatedLineItem.related_shipment
                ? updatedLineItem
                : item
            )
          : old
      );
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      if (prevPo !== updatedLineItem.title) {
        // PO rename cascades to child parts (commit 0ef36ff).
        queryClient.invalidateQueries({ predicate: q => q.queryKey[0] === 'parts' });
        // Keep the URL and identityList coherent under the new PO so refresh
        // and chevrons resolve to the right row.
        const newIdentityList = identityList.map((id, i) =>
          i === identityIndex
            ? { po: updatedLineItem.title, shipment: updatedLineItem.related_shipment }
            : id
        );
        const params = new URLSearchParams({
          po: updatedLineItem.title,
          shipment: updatedLineItem.related_shipment,
        });
        navigate(`/line-item?${params}`, {
          replace: true,
          state: {
            ...location.state,
            identityList: newIdentityList,
          },
        });
      }

      markDataChanged();
    } catch (err) {
      console.error('Error updating line item:', err);
      const errorMessage = err.response?.data?.message
        || 'Failed to update line item. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEdit = () => {
    if (isEditing) {
      // Discard changes
      setFormData({
        ...lineItem,
        poNumber: lineItem.title // Ensure poNumber is set to title when canceling edit
      });
    } else {
      // Entering edit mode - ensure poNumber is set
      setFormData({
        ...formData,
        poNumber: lineItem.title // Ensure poNumber is set to title when entering edit mode
      });
    }
    setIsEditing(!isEditing);
  };

  // Chevron navigation walks the identityList in location.state. No network
  // calls — each new identity is just looked up in the bulk lineItems prop.
  const goToIdentity = (newIdx) => {
    const target = identityList[newIdx];
    if (!target) return;
    const params = new URLSearchParams({ po: target.po, shipment: target.shipment });
    navigate(`/line-item?${params}`, {
      state: { ...location.state, identityIndex: newIdx },
    });
  };

  const goToPrevious = () => {
    if (identityIndex > 0) goToIdentity(identityIndex - 1);
  };

  const goToNext = () => {
    if (identityIndex >= 0 && identityIndex < identityList.length - 1) {
      goToIdentity(identityIndex + 1);
    }
  };

  const handleEditClick = (part) => {
    setEditingPart(part);
    setEditQuantity(part.qty?.toString() || '');
  };

  const handleSavePart = async () => {
    if (!canEditLineItems()) return;
    setError(null);
    try {
      if (!editingPart) return;
      
      const updatedPart = {
        ...editingPart,
        qty: parseInt(editQuantity)
      };
      
      // Update local state - make sure part_numbers is defined first
      if (!lineItem.part_numbers) {
        setLineItem({
          ...lineItem,
          part_numbers: [updatedPart]
        });
      } else {
        const updatedParts = lineItem.part_numbers.map(p =>
          p.part_number === editingPart.part_number
            ? { ...updatedPart, _isModified: !p._isNew } // Mark as modified if not new
            : p
        );
        setLineItem({
          ...lineItem,
          part_numbers: updatedParts
        });
      }

      setEditingPart(null);
      setEditQuantity('');
      setHasUnsavedPartChanges(true);
      markDataChanged(); // Mark data as changed
    } catch (error) {
      console.error('Error updating part:', error);
      setError('Failed to update part. Please try again.');
    }
  };

  // Handler for adding parts from the popup
  const handleAddParts = (newPart) => {
    setLineItem(prev => {
      const currentParts = prev.part_numbers || [];
      // Mark as new so we know to use createParts API
      return {
        ...prev,
        part_numbers: [...currentParts, { ...newPart, _isNew: true }]
      };
    });
    setHasUnsavedPartChanges(true);
  };

  // Add a new handler for saving part changes
  const handleSavePartChanges = async () => {
    if (!canEditLineItems()) return;
    setIsSavingParts(true);
    try {
      if (!lineItem.part_numbers) {
        console.error("No parts to save");
        setError("No parts data to save. Please add parts first.");
        return;
      }

      // Separate new parts from existing modified parts
      const newParts = lineItem.part_numbers.filter(p => p._isNew);
      const existingParts = lineItem.part_numbers.filter(p => !p._isNew && p._isModified);

      console.log("New parts to create:", newParts);
      console.log("Existing parts to update:", existingParts);

      // Call appropriate APIs
      if (newParts.length > 0) {
        // Remove _isNew flag before sending to API
        const partsToCreate = newParts.map(({ _isNew, _isModified, ...part }) => part);
        await utils.createParts(partsToCreate);
      }

      if (existingParts.length > 0) {
        // Remove flags before sending to API
        const partsToUpdate = existingParts.map(({ _isNew, _isModified, ...part }) => part);
        await utils.updateParts(partsToUpdate);
      }

      // After successful save, remove _isNew and _isModified flags from local state
      setLineItem(prev => ({
        ...prev,
        part_numbers: prev.part_numbers.map(({ _isNew, _isModified, ...part }) => part)
      }));

      setHasUnsavedPartChanges(false);
      markDataChanged();
    } catch (error) {
      setHasUnsavedPartChanges(true);
      console.error('Error saving part changes:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save part changes. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSavingParts(false);
    }
  };

  const handleDeletePart = (part) => {
    if (!canEditLineItems() || !part) return;
    setError(null);
    setPartToDelete(part);
  };

  const handleConfirmDeletePart = async () => {
    if (!canEditLineItems()) return;
    if (partToDelete && lineItem.part_numbers) {
      setIsDeletingPart(true);
      try {
        setError(null);
        await utils.deletePart({
              part_number: partToDelete.part_number,
              related_shipment: partToDelete.related_shipment || lineItem.related_shipment,
              related_po_number: partToDelete.related_po_number || lineItem.title,
              prev_part_numbers: lineItem.part_numbers
            });

        // Filter out the deleted part
        const updatedParts = lineItem.part_numbers.filter(
          p => p.part_number !== partToDelete.part_number
        );

        // Create updated line item
        const updatedLineItem = {
          ...lineItem,
          part_numbers: updatedParts
        };

        // Update local state
        setLineItem(updatedLineItem);

        // Optimistically patch the bulk list so the form's lookup effect
        // doesn't briefly revert to the pre-delete state during refetch.
        queryClient.setQueriesData(
          { queryKey: ['lineItems'] },
          (old) => Array.isArray(old)
            ? old.map(item =>
                item.title === lineItem.title && item.related_shipment === lineItem.related_shipment
                  ? updatedLineItem
                  : item
              )
            : old
        );
        queryClient.invalidateQueries({ queryKey: ['lineItems'] });
        queryClient.invalidateQueries({ predicate: q => q.queryKey[0] === 'parts' });

        setPartToDelete(null);
        markDataChanged(); // Mark data as changed
      } catch (error) {
        console.error('Error deleting part:', error);
        const errorMessage = error.response?.data?.message || 'Failed to delete part. Please try again.';
        setError(errorMessage);
      } finally {
        setIsDeletingPart(false);
      }
    }
    setShowDeleteConfirm(false);
  };

  const handleDeleteLineItem = async () => {
    try {
      setError(null);
      setIsDeleting(true);

      await utils.deleteLineItem({
        title: lineItem.title,
        related_shipment: lineItem.related_shipment
      });

      // Refetch the bulk list so the dashboard reflects the deletion.
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ predicate: q => q.queryKey[0] === 'parts' });

      // Navigate back to the appropriate page
      if (location.state?.fromShipment && location.state?.shipmentId) {
        navigate(`/shipment-details/${location.state.shipmentId}`);
      } else {
        navigate('/');
      }
      markDataChanged(); // Mark data as changed
    } catch (error) {
      console.error('Error deleting line item:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete line item. Please try again.';
      setError(errorMessage);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmToggleHide = async () => {
    if (!canEditLineItems()) return;
    setError(null);
    setIsTogglingHide(true);
    try {
      const newHidden = !(lineItem.filter_record_out || lineItem.hide_lineitem);
      const prevPo = lineItem.title;
      const updatedLineItem = {
        ...lineItem,
        hide_lineitem: newHidden,
        filter_record_out: newHidden,
      };

      const query = new URLSearchParams({
        shipment: lineItem.related_shipment,
        po: prevPo,
      }).toString();
      await utils.updateLineItem(updatedLineItem, query);

      setLineItem(updatedLineItem);
      setFormData((prev) => ({
        ...prev,
        filter_record_out: newHidden,
      }));

      queryClient.setQueriesData(
        { queryKey: ['lineItems'] },
        (old) => Array.isArray(old)
          ? old.map(item =>
              item.title === prevPo && item.related_shipment === updatedLineItem.related_shipment
                ? updatedLineItem
                : item
            )
          : old
      );
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });

      markDataChanged();
      setShowHideConfirm(false);
    } catch (err) {
      console.error('Error toggling hide state:', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to update record visibility. Please try again.';
      setError(errorMessage);
    } finally {
      setIsTogglingHide(false);
    }
  };

  const renderDocumentsTab = () => {
    if (loadingDocuments) {
      return (
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-full"></div>
          <div className="h-10 bg-gray-200 rounded w-full"></div>
          <div className="h-10 bg-gray-200 rounded w-full"></div>
        </div>
      );
    }

    if (documentsError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <AlertCircle size={20} className="text-red-500 mr-2" />
            <p className="text-red-700">{documentsError}</p>
          </div>
        </div>
      );
    }

    return (
      <div>
        {lineItem && (
          <ShipmentDocuments documents={documents} shipment={lineItem.related_shipment} forwarder={lineItem.forwarder_name} />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-6 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-4 bg-gray-200 rounded col-span-2"></div>
                <div className="h-4 bg-gray-200 rounded col-span-1"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lineItem) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Line item not found. The PO number may be incorrect, deleted, or marked as hidden.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dates', label: 'Dates', icon: <Calendar size={16} className="mr-1" /> },
    { id: 'container', label: 'Container Details', icon: <Package size={16} className="mr-1" /> },
    { id: 'parts', label: 'Parts', icon: <Truck size={16} className="mr-1" /> },
    { id: 'documents', label: 'Documents', icon: <ExternalLink size={16} className="mr-1" /> },
  ];

  // Reusable field
  const renderField = (label, value, name, icon = null, isEditable = false) => {
    const isEditableField = isEditable && isEditing;
    const isShipmentField = name === 'shipment';
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
          {icon && <span className="mr-1.5 text-gray-400">{icon}</span>}
          {label}
        </label>
        <div
          className={cn(
            'rounded-md overflow-hidden transition-all duration-200',
            isEditableField ? '' : ''
          )}
        >
          {isEditableField ? (
            <input
              type="text"
              name={name}
              value={formData[name] || ''}
              onChange={handleInputChange}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none bg-white"
              autoFocus
            />
          ) : (
            <div
              className={cn(
                'p-2.5 border rounded-md flex items-center',
                'bg-gray-50 border-gray-200 text-gray-800'
              )}
            >
              {isShipmentField ? (
                <button
                  onClick={() => navigate(`/shipment-details/${value}`, {
                    state: {
                      lineItemPo: lineItem.title,
                      lineItemShipment: lineItem.related_shipment,
                      lineItemReturnState: {
                        identityList,
                        identityIndex,
                      },
                    },
                  })}
                  className="text-gray-800 hover:text-blue-600 hover:underline transition-colors focus:outline-none"
                  title="View shipment"
                >
                  {value || '-'}
                </button>
              ) : (
                <span>{value || '-'}</span>
              )}
              {isEditable && isEditing && (
                <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                  Click Edit to modify
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (location.state?.fromShipment && location.state?.shipmentId) {
                navigate(`/shipment-details/${location.state.shipmentId}`);
              } else {
                navigate('/');
              }
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Line Item: {lineItem.poNumber || lineItem.title}
            </h1>
            <div className="flex items-center mt-1">
              <span className="text-gray-600 mr-2">Shipment:</span>
              <button
                onClick={() => navigate(`/shipment-details/${lineItem.related_shipment || ''}`, {
                  state: {
                    lineItemPo: lineItem.title,
                    lineItemShipment: lineItem.related_shipment,
                    lineItemReturnState: {
                      identityList,
                      identityIndex,
                    },
                  },
                })}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors border border-accent-200 shadow-sm"
              >
                {lineItem.related_shipment || 'N/A'}
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-2">
          {hasNeighbors && (
            <div className="flex items-center justify-end space-x-2">
              <button
                onClick={goToPrevious}
                disabled={identityIndex <= 0}
                className={`p-2 rounded-full transition-colors ${
                  identityIndex <= 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Previous line item"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm text-gray-500">
                {identityIndex + 1} of {identityList.length}
              </span>
              <button
                onClick={goToNext}
                disabled={identityIndex >= identityList.length - 1}
                className={`p-2 rounded-full transition-colors ${
                  identityIndex >= identityList.length - 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Next line item"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
          <div className="flex items-center space-x-2">
            {canEditLineItems() && (
              <>
                {!isForwarderRole() && (
                  <button
                    onClick={() => setShowHideConfirm(true)}
                    disabled={isDeleting || isTogglingHide}
                    className={cn(
                      'px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-colors',
                      'border border-gray-300 text-gray-700 hover:bg-gray-50',
                      (isDeleting || isTogglingHide) && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    {isTogglingHide ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (lineItem.filter_record_out || lineItem.hide_lineitem) ? (
                      <Eye size={16} />
                    ) : (
                      <EyeOff size={16} />
                    )}
                    <span>
                      {isTogglingHide
                        ? 'Saving...'
                        : (lineItem.filter_record_out || lineItem.hide_lineitem)
                          ? 'Unhide Record'
                          : 'Hide Record'}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  className={cn(
                    'px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-colors',
                    'border border-red-600 text-red-600 hover:bg-red-50',
                    isDeleting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <Trash2 size={16} />
                  <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
                </button>
                <button
                  onClick={toggleEdit}
                  disabled={isDeleting}
                  className={cn(
                    'px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-colors',
                    isEditing
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'bg-accent-600 text-white hover:bg-accent-700',
                    isDeleting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isEditing ? (
                    <>
                      <X size={16} />
                      <span>Cancel</span>
                    </>
                  ) : (
                    <>
                      <Edit size={16} />
                      <span>Edit</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-3 flex items-center">
          <AlertCircle size={16} className="text-red-500 mr-2" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isEditing && (
          <div className="bg-blue-50 p-4 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <p className="text-blue-700 font-medium flex items-center">
                <Edit size={16} className="mr-2" />
                You can edit the PO # field
              </p>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={cn(
                  "px-4 py-2 bg-accent-600 rounded-md text-sm font-medium text-white flex items-center space-x-1",
                  isSaving ? "opacity-50 cursor-not-allowed" : "hover:bg-accent-700"
                )}
              >
                {isSaving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        )}

        <div className="p-6">
          {/* Top section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div>
              {(lineItem.po_review_required > 10 || (lineItem.title?.length ?? 0) < 5) && !isForwarderRole() && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
                    <span className="mr-1.5 text-gray-400"><Package size={16} /></span>
                    Valid PO #
                  </label>
                  <div className={cn(
                    'rounded-md overflow-hidden transition-all duration-200',
                  )}>
                    <div className={cn(
                      'p-2.5 border rounded-md flex items-center',
                      'bg-gray-50 border-gray-200 text-gray-800'
                    )}>
                      <input
                        type="checkbox"
                        id="ignore_po_character_count"
                        checked={formData.ignore_po_character_count || false}
                        onChange={(e) => {
                          if (isEditing && canEditLineItems()) {
                            setFormData({
                              ...formData,
                              ignore_po_character_count: e.target.checked
                            });
                            if (lineItem) {
                              setLineItem({
                                ...lineItem,
                                ignore_po_character_count: e.target.checked
                              });
                            }
                          }
                        }}
                        disabled={!isEditing || !canEditLineItems()}
                        className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-gray-700">
                        {formData.ignore_po_character_count ? 'Yes' : 'No'}
                      </span>
                      {isEditing && canEditLineItems() ? null : (
                        <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {canEditLineItems() ? 'Click Edit to modify' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {renderField('PO #', lineItem.title, 'poNumber', <Package size={16} />, true)}
            </div>
            {renderField('Shipment', lineItem.related_shipment, 'shipment', <Truck size={16} />)}
            {renderField('Forwarder Name', lineItem.forwarder_name, 'forwarderName', <ExternalLink size={16} />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {renderField(
              'Shipment: Consignee Name',
              lineItem.cosignee_name,
              'consigneeName'
            )}
            {renderField(
              'Shipment: Consignor Name',
              lineItem.consignor_name,
              'consignorName'
            )}
            {renderField(
              'Shipment: Origin Port Code',
              lineItem.origin_port,
              'originPortCode'
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {renderField(
              'Shipment: Destination Port Code',
              lineItem.destination_port,
              'destinationPort'
            )}
            {renderField('Shipment: Master', lineItem.master, 'master')}
            {renderField('Shipment: House', lineItem.house, 'house')}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center',
                    activeTab === tab.id
                      ? 'border-accent-500 text-accent-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="mt-6">
            {activeTab === 'dates' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {renderField(
                  'Shipment: OBL Received Date',
                  lineItem.obl_received_date || '-',
                  'obl_received_date',
                  <Calendar size={16} />
                )}
                {renderField('Shipment: ATD', lineItem.atd || '-', 'atd', <Calendar size={16} />)}
                {renderField(
                  'Shipment: Dest. ETA',
                  lineItem.dest_eta || '-',
                  'dest_eta',
                  <Calendar size={16} />
                )}
                {renderField('Shipment: ATA', lineItem.ata || '-', 'ata', <Calendar size={16} />)}
                {renderField(
                  'Shipment: Est Delivery',
                  lineItem.est_delivery || '-',
                  'est_delivery',
                  <Calendar size={16} />
                )}
                {renderField(
                  'Shipment: Act Delivery',
                  lineItem.act_delivery || '-',
                  'act_delivery',
                  <Calendar size={16} />
                )}
                {renderField(
                  'Shipment: Origin ETD',
                  lineItem.shipment_origin_etd || '-',
                  'shipment_origin_etd',
                  <Calendar size={16} />
                )}
              </div>
            )}

            {activeTab === 'container' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {renderField(
                  'Shipment: Weight - Formula',
                  lineItem.weight || '—',
                  'weight',
                  <Package size={16} />
                )}
                {renderField(
                  'Shipment: Outer - Formula',
                  lineItem.outer || '—',
                  'outer',
                  <Package size={16} />
                )}
                {renderField(
                  'Shipment: Volume - Formula',
                  lineItem.volume || '—',
                  'volume',
                  <Package size={16} />
                )}
                {renderField(
                  'Shipment: Charge - Formula',
                  '0.00 M3',
                  'charge',
                  <Package size={16} />
                )}
              </div>
            )}

            {activeTab === 'parts' && (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="flex justify-between items-center bg-gray-50 border-b border-gray-200 p-3">
                  <div className="text-sm font-medium text-gray-700">Parts Information</div>
                  <div className="flex items-center space-x-2">
                    {canEditLineItems() && (
                      <button 
                        className="flex items-center text-xs font-medium text-white bg-accent-600 hover:bg-accent-700 px-3 py-1.5 rounded transition-colors"
                        onClick={() => setShowPartsPopup(true)}
                      >
                        <Plus size={14} className="mr-1" />
                        Add Parts
                      </button>
                    )}
                    {hasUnsavedPartChanges && canEditLineItems() && (
                      <button
                        onClick={handleSavePartChanges}
                        disabled={isSavingParts}
                        className={`flex items-center text-xs font-medium text-white px-3 py-1.5 rounded transition-colors mr-2 ${
                          isSavingParts ? 'bg-accent-400 cursor-not-allowed' : 'bg-accent-600 hover:bg-accent-700'
                        }`}
                      >
                        {isSavingParts ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <Save size={14} className="mr-1" />
                        )}
                        {isSavingParts ? 'Saving...' : 'Save Changes'}
                      </button>
                    )}
                  </div>
                </div>
                {lineItem.part_numbers && lineItem.part_numbers.length > 0 ? (
                  <div>
                    <div className="bg-gray-50 border-b border-gray-200">
                      <div className="grid grid-cols-12 text-xs font-medium text-gray-600 p-3">
                        <div className="col-span-4">Part Number</div>
                        <div className="col-span-3">Quantity</div>
                        <div className="col-span-3">PO Number</div>
                        <div className="col-span-1">Shipment</div>
                        <div className="col-span-1 text-right">Actions</div>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {lineItem.part_numbers.map((part, index) => (
                        <div 
                          key={index}
                          className={`grid grid-cols-12 p-3 text-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        >
                          <div className="col-span-4 font-medium text-gray-700">{part.part_number}</div>
                          <div className="col-span-3 text-gray-700">{part.qty ? part.qty.toLocaleString() : 'N/A'}</div>
                          <div className="col-span-3 text-gray-600">{part.related_po_number || '—'}</div>
                          <div className="col-span-1">
                            {part.related_shipment ? (
                              <button
                                onClick={() => navigate(`/shipment-details/${part.related_shipment}`, {
                                  state: {
                                    lineItemPo: lineItem.title,
                                    lineItemShipment: lineItem.related_shipment,
                                    lineItemReturnState: {
                                      identityList,
                                      identityIndex,
                                    },
                                  },
                                })}
                                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                              >
                                {part.related_shipment}
                              </button>
                            ) : (
                              '—'
                            )}
                          </div>
                          <div className="col-span-1 text-right flex items-center justify-end space-x-1">
                            {canEditLineItems() && (
                              <>
                                <button
                                  onClick={() => handleDeletePart(part)}
                                  className="p-1 text-gray-500 hover:text-red-600 rounded-full hover:bg-gray-100 transition-colors"
                                  title="Delete part"
                                >
                                  <Trash2 size={16} />
                                </button>
                                <button
                                  onClick={() => handleEditClick(part)}
                                  className="p-1 text-gray-500 hover:text-accent-600 rounded-full hover:bg-gray-100 transition-colors"
                                  title="Edit part"
                                >
                                  <Pencil size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 border-t border-gray-200 p-3 text-xs text-gray-500 flex justify-between">
                      <span>Total Parts: {lineItem.part_numbers.length}</span>
                      <span>
                        Total Quantity:{' '}
                        {lineItem.part_numbers.reduce((sum, part) => sum + (parseInt(part.qty) || 0), 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-8 text-center">
                    <Truck size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">No parts information available</p>
                    {canEditLineItems() && (
                      <button 
                        className="mt-4 flex items-center mx-auto text-xs font-medium text-white bg-accent-600 hover:bg-accent-700 px-3 py-1.5 rounded transition-colors"
                        onClick={() => {
                          // Add parts functionality would go here
                          setShowPartsPopup(true);
                        }}
                      >
                        <Plus size={14} className="mr-1" />
                        Add
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'documents' && renderDocumentsTab()}
          </div>
        </div>
      </div>

      {editingPart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 max-w-full">
            <div className="flex justify-between items-center border-b border-gray-200 p-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Part: {editingPart.part_number}
              </h3>
              <button
                onClick={() => setEditingPart(null)}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity
              </label>
              <input
                type="number"
                value={editQuantity}
                onChange={(e) => setEditQuantity(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                placeholder="Enter quantity"
                min="0"
              />
            </div>

            <div className="bg-gray-50 px-4 py-3 flex justify-end space-x-2 rounded-b-lg">
              <button
                onClick={() => setEditingPart(null)}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                <X size={16} className="inline-block mr-1" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSavePart}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 border border-transparent rounded-md hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                disabled={!editQuantity || parseInt(editQuantity) < 0}
              >
                <Save size={16} className="inline-block mr-1" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationPopup
        isOpen={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        onConfirm={handleDeleteLineItem}
        title="Delete Line Item"
        message={`Are you sure you want to delete line item "${lineItem.title}"? This action cannot be undone and will also remove all associated parts.`}
      />

      {/* Delete Part Confirmation */}
      <DeleteConfirmationPopup
        isOpen={!!partToDelete}
        onClose={() => !isDeletingPart && setPartToDelete(null)}
        onConfirm={handleConfirmDeletePart}
        title="Delete Part"
        message={`Are you sure you want to delete part ${partToDelete?.part_number}? This action cannot be undone.`}
        isLoading={isDeletingPart}
      />

      {/* Hide/Unhide Line Item Confirmation */}
      <DeleteConfirmationPopup
        isOpen={showHideConfirm}
        onClose={() => !isTogglingHide && setShowHideConfirm(false)}
        onConfirm={handleConfirmToggleHide}
        title={
          (lineItem.filter_record_out || lineItem.hide_lineitem)
            ? 'Unhide Line Item'
            : 'Hide Line Item'
        }
        message={
          (lineItem.filter_record_out || lineItem.hide_lineitem)
            ? `Are you sure you want to unhide line item "${lineItem.title}"? It will reappear in your dashboard.`
            : `Are you sure you want to hide line item "${lineItem.title}"? It will be filtered out of your dashboard.`
        }
        isLoading={isTogglingHide}
        confirmLabel={(lineItem.filter_record_out || lineItem.hide_lineitem) ? 'Unhide' : 'Hide'}
        confirmLoadingLabel="Saving..."
        confirmIcon={
          (lineItem.filter_record_out || lineItem.hide_lineitem)
            ? <Eye size={16} />
            : <EyeOff size={16} />
        }
        confirmVariant="accent"
      />

      {/* Parts Popup */}
      <PartsPopup
        parts={lineItem?.part_numbers?.length > 0 ? lineItem.part_numbers : []}
        isOpen={showPartsPopup}
        onClose={() => {
          setShowPartsPopup(false);
          // setHasUnsavedPartChanges(false);
        }}
        poNumber={lineItem?.title}
        shipment={lineItem?.related_shipment}
        onAddParts={handleAddParts}
        onRevertParts={(originalParts) => {
          // Revert parent state to original parts on save failure
          setLineItem(prev => ({ ...prev, part_numbers: originalParts }));
          setHasUnsavedPartChanges(false);
        }}
        isFromDashboard={true}
        onSaveChanges={async (parts) => {
          // Separate new parts from existing modified parts
          const newParts = parts.filter(p => p._isNew);
          const existingParts = parts.filter(p => !p._isNew && p._isModified);

          // Call appropriate APIs
          if (newParts.length > 0) {
            const partsToCreate = newParts.map(({ _isNew, _isModified, ...part }) => part);
            await utils.createParts(partsToCreate);
          }

          if (existingParts.length > 0) {
            const partsToUpdate = existingParts.map(({ _isNew, _isModified, ...part }) => part);
            await utils.updateParts(partsToUpdate);
          }

          // Remove _isNew and _isModified flags from local state
          const cleanedParts = parts.map(({ _isNew, _isModified, ...part }) => part);
          setLineItem(prev => ({ ...prev, part_numbers: cleanedParts }));
          setHasUnsavedPartChanges(false);
        }}
      />
    </div>
  );
};

export default LineItemForm;
